// ============================================================================
// Alert Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base alert component for notifications
// ============================================================================

import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * Alert variants
 */
export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

/**
 * Alert props
 */
export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  icon?: ReactNode;
  action?: ReactNode;
  isDismissible?: boolean;
  onDismiss?: () => void;
  children?: ReactNode;
}

/**
 * Base styles for alerts
 */
const baseStyles = 'rounded-lg p-4 flex';

/**
 * Variant styles
 */
const variantStyles: Record<AlertVariant, string> = {
  info: `
    bg-blue-50 text-blue-800 border border-blue-200
    dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800
  `,
  success: `
    bg-green-50 text-green-800 border border-green-200
    dark:bg-green-900/20 dark:text-green-300 dark:border-green-800
  `,
  warning: `
    bg-yellow-50 text-yellow-800 border border-yellow-200
    dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800
  `,
  error: `
    bg-red-50 text-red-800 border border-red-200
    dark:bg-red-900/20 dark:text-red-300 dark:border-red-800
  `,
};

/**
 * Default icons for each variant
 */
const defaultIcons: Record<AlertVariant, ReactNode> = {
  info: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
  success: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  warning: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

/**
 * Dismiss button icon
 */
const DismissIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

/**
 * Alert Component
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant = 'info',
      title,
      icon,
      action,
      isDismissible = false,
      onDismiss,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const effectiveIcon = icon ?? defaultIcons[variant];

    return (
      <div
        ref={ref}
        role="alert"
        className={clsx(baseStyles, variantStyles[variant], className)}
        {...props}
      >
        {effectiveIcon && (
          <div className="flex-shrink-0 mr-3">{effectiveIcon}</div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-sm font-medium mb-1">{title}</h3>
          )}
          {children && (
            <div className="text-sm opacity-90">{children}</div>
          )}
        </div>
        {action && <div className="flex-shrink-0 ml-4">{action}</div>}
        {isDismissible && (
          <button
            type="button"
            className="flex-shrink-0 ml-4 inline-flex rounded-md p-1.5 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <DismissIcon />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export default Alert;
